import AppNavBar from "@/layout/app-bar";
import { CustomTypography, ThemePalettes } from "@/services/theme-palettes";
import Components from "./styles";
import {
  setCountriesOptions,
  setGeneralState,
  setLeadOriginTypesToState,
  setStatesOptions,
  setTimezonesToState,
} from "@/store/slices/general/generalSlice";
import { CircularProgress, createTheme, ThemeProvider } from "@mui/material";
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";
import { useSelector } from "react-redux";
import { ToastContainer, Zoom } from "react-toastify";
import { useEffect, useMemo, useState } from "react";
import SessionExpirePrompt from "@/components/session-expire-prompt";
import { globalLogout } from "@/services/generic-logout-method";
import {
  getLoggedinUser,
  selectSessionExpired,
  setUpdatedProfileToState,
  setUserCompaniesCountToState,
} from "@/store/slices/auth/authSlice";
import {
  getOptionsForCreateLead,
  setDomainEmailsToState,
  setMembersToState,
} from "@/store/slices/general/dropdownOptionsSlice";
import dropdownOptionsQueries from "./global/dropdownOptionsQueries";
import { useDispatch } from "react-redux";
import { getLeadHashList } from "./settings/components/details/content/automation-templates/sms-templates/helper";
import {
  setAppointmentSearchFields,
  setCustomCodesToState,
  setEmailSearchFields,
  setLeadsSearchFields,
  setLeadUpdateFields,
  setSMSSearchFields,
} from "@/store/slices/lead/crmLeadSlice";
import { Get_Custom_Codes } from "./leads/graphql-queries/sms-template-queries";
import UseGql from "@/services/graph-ql/useGql";
import { Get_TimeZones } from "./leads/graphql-queries/timezone";
import {
  fetchPaginatedData,
  generateOptions,
  makeObjectFromArray,
} from "@/services/generic.methods";
import {
  Get_Appointment_Search_Fields,
  Get_Companies,
  Get_Email_Search_Fields,
  Get_Lead_Search_Fields,
  Get_Lead_Update_Fields,
  Get_SMS_Search_Fields,
} from "./leads/graphql-queries";
import {
  setTriggerCategoriesListToState,
  setTriggersListToState,
} from "@/store/slices/workflow/workflowSlice";
import {
  Get_Company_Business_Category,
  Get_WF_Triggers,
  Get_WF_Triggers_Category,
} from "./automation/graphql-queries";
import TourWrapper from "@/components/tour-guide";
import { Get_Domain_Emails } from "./leads/graphql-queries/email-integration";
import { Get_Appointment_Members } from "./leads/graphql-queries/appointment-scheduling";
import useManageSMSPermission from "./settings/components/details/content/roles-and-permissions/sms/useManagePermission";
import {
  Get_Countries,
  Get_Lead_Origin_Types,
  Get_States,
} from "@/services/graph-ql/queries";
import useCRMTheme from "@/hooks/useCRMTheme";
import useUserSettings, { SettingsKey } from "@/hooks/useUserSettings";
import UilCircularLoader from "@/components/circular-loader-uil";
import useDesktopNotification from "@/hooks/useDesktopNotification";

export default function CRMLayout({ children }) {
  const [showSessionModal, setShowSessionModal] = useState(false);
  const user = useSelector(getLoggedinUser);
  const sessionExpired = useSelector(selectSessionExpired);
  const [loading, setLoading] = useState(true);
  const { themeInfo } = useCRMTheme();
  const { settings, getUserSettings } = useUserSettings();
  const dispatch = useDispatch();
  const { executeQuery } = UseGql(Get_Custom_Codes, {
    skip: 0,
    take: 30,
  });
  const getLeadFields = UseGql(Get_Lead_Search_Fields);
  const getLeadUpdateFields = UseGql(Get_Lead_Update_Fields);
  const getSMSFields = UseGql(Get_SMS_Search_Fields);
  const getEmailFields = UseGql(Get_Email_Search_Fields);
  const getAppointmentFields = UseGql(Get_Appointment_Search_Fields);
  const getTimeZones = UseGql(Get_TimeZones);
  const getTriggersCategory = UseGql(Get_WF_Triggers_Category);
  const getTriggers = UseGql(Get_WF_Triggers);
  const getCompanyBC = UseGql(Get_Company_Business_Category);
  const fetchUserCompanies = UseGql(Get_Companies);
  const fetchDomainEmails = UseGql(Get_Domain_Emails);
  const getMembersGql = UseGql(Get_Appointment_Members);
  const getCountries = UseGql(Get_Countries);
  const getStates = UseGql(Get_States);
  const getLeadOriginTypes = UseGql(Get_Lead_Origin_Types);
  const { getSMSPermission } = useManageSMSPermission();
  const { requestPermission } = useDesktopNotification();
  const theme = createTheme({
    palette: ThemePalettes[themeInfo?.mode],
    typography: CustomTypography,
    components: {
      MuiInputBase: { styleOverrides: { root: { fontSize: "0.875rem" } } },
      MuiInputLabel: { styleOverrides: { root: { fontSize: "0.875rem" } } },
      ...Components,
    },
  });

  useEffect(() => {
    // Inject CSS variables into the :root
    const root = document.documentElement;
    if (themeInfo && theme?.palette?.custom) {
      Object.entries(theme?.palette?.custom).forEach(([key, value]) => {
        root.style.setProperty(`--palette-${key}`, value);
      });
    }
  }, [themeInfo]);

  useEffect(() => {
    if (settings?.value && settings.key === SettingsKey.user_theme) {
      dispatch(setGeneralState({ theme: settings?.value }));
    }
  }, [settings?.value]);

  // fetch custom Codes
  const fetchCustomCodes = () => {
    executeQuery().then((res) => {
      let { data } = res;
      if (data?.customCode?.items) {
        dispatch(
          setCustomCodesToState(getLeadHashList(data?.customCode?.items))
        );
      }
    });
  };
  // fetch lead search fields
  const fetchLeadSearchFields = () => {
    let filterKeys = ["LeadOriginTypeId", "State", "Country"];
    getLeadFields.executeQuery().then((res) => {
      let { data } = res;

      if (data?.leadSearchFields?.items) {
        let fields = data?.leadSearchFields?.items?.filter(
          (v) => !filterKeys.includes(v.key)
        );

        dispatch(setLeadsSearchFields(fields));
      }
    });
  };

  // fetch lead update fields for updating from workflow or any other area
  const fetchLeadUpdateFields = () => {
    getLeadUpdateFields.executeQuery().then((res) => {
      let { data } = res;

      if (data?.leadUpdateFields?.items) {
        let fields = data?.leadUpdateFields?.items;
        dispatch(setLeadUpdateFields(fields));
      }
    });
  };

  // fetch sms search fields
  const fetchSMSSearchFields = () => {
    getSMSFields.executeQuery().then((res) => {
      let { data } = res;
      if (data?.smsSearchFields?.items) {
        dispatch(setSMSSearchFields(data?.smsSearchFields?.items));
      }
    });
  };
  // fetch email search fields
  const fetchEmailSearchFields = () => {
    getEmailFields.executeQuery().then((res) => {
      let { data } = res;
      if (data?.emailSearchFields?.items) {
        dispatch(setEmailSearchFields(data?.emailSearchFields?.items));
      }
    });
  };

  // fetch appointment search fields
  const fetchAppointmentSearchFields = () => {
    getAppointmentFields.executeQuery().then((res) => {
      let { data } = res;
      if (data?.appointmentSearchFields?.items) {
        dispatch(
          setAppointmentSearchFields(data?.appointmentSearchFields?.items)
        );
      }
    });
  };

  // fetch Categories
  const fetchCategories = () => {
    getTriggersCategory.executeQuery().then((res) => {
      let { data } = res;
      dispatch(
        setTriggerCategoriesListToState(
          data?.workFlowTriggerCategories?.items || []
        )
      );
    });
  };

  // fetch company Category
  const fetchCompanyCategory = (companyKey) => {
    getCompanyBC.executeQuery({ companyKey }).then((res) => {
      let { data } = res;
      if (data?.companyBusinessCategory?.items) {
        let category = {
          businessCategory: data?.companyBusinessCategory?.items[0] || {},
        };
        dispatch(setUpdatedProfileToState(category || {}));
      }
    });
  };
  // fetch Triggers
  const fetchTriggers = () => {
    getTriggers.executeQuery().then((res) => {
      let { data } = res;
      dispatch(setTriggersListToState(data?.workFlowTriggers?.items));
    });
  };

  // fetch timezones
  const fetchTimeZones = () => {
    getTimeZones.executeQuery().then((res) => {
      if (res && res?.data && res?.data?.timeZones) {
        let result = res?.data?.timeZones;
        let timezonesOptions = generateOptions(result, "value", "key");
        timezonesOptions = timezonesOptions?.map((item) => ({
          ...item,
          key: null,
        }));
        dispatch(setTimezonesToState(timezonesOptions));
      }
    });
  };

  // get user total companies count for show/hide switch company option
  const getCompanies = () => {
    let userEmail = user?.Email;
    return fetchUserCompanies
      .executeQuery({ userEmail })
      .then((response) => {
        const { data } = response;
        if (data && data?.userCompanies) {
          let count = data?.userCompanies.length;
          dispatch(setUserCompaniesCountToState(count));
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  // get domains emails to show in dropdowns in several areas
  const getDomainEmails = () => {
    fetchDomainEmails
      .executeQuery()
      .then((res) => {
        let { data } = res;
        if (data?.domainEmail && data?.domainEmail.items) {
          let allDomainEmails = data?.domainEmail?.items;
          let options = generateOptions(
            allDomainEmails,
            "emailAddress",
            "emailAddress"
          );
          options = options.map((opt) => {
            opt.groupByName = "Email Sending from Domains";
            return opt;
          });
          dispatch(setDomainEmailsToState(options));
        }
      })
      .catch((err) => console.log("onError:", err));
  };

  // get appoinment related members
  const fetchMembers = async () => {
    await fetchPaginatedData({
      gqlQuery: getMembersGql,
      dataKey: "members",
      // setData: setMembers,
      dispatch: (items) => dispatch(setMembersToState(items)),
      // setFetchingDone: setMembersFetchingDone,
    });
  };

  useEffect(() => {
    let onStorageChange = (event) => {
      if (event.key == "Profile" && event.newValue == null) {
        setShowSessionModal(true);
      }
    };
    // add event listener for storage change
    window.addEventListener("storage", onStorageChange);

    // remove event listener for storage change
    return () => {
      window.removeEventListener("storage", onStorageChange);
    };
  }, []);

  const logoutHandler = () => {
    // send to login page
    let shouldRedirect = true;
    globalLogout(shouldRedirect);
    setShowSessionModal(false);
  };

  const getOptions = async () => {
    setLoading(true);
    const param = {
      companyId: user.CompanyID,
      searchText: "",
    };
    await dispatch(
      getOptionsForCreateLead({ request: dropdownOptionsQueries(param) })
    );
    setLoading(false);
  };

  useEffect(() => {
    if (sessionExpired) {
      setShowSessionModal(true);
    }
  }, [sessionExpired]);

  useEffect(() => {
    if (user?.CompanyID) {
      getOptions();
      getCompanies();
    }
  }, [user?.CompanyID]);

  useEffect(() => {
    fetchCustomCodes();
    fetchTimeZones();
    fetchEmailSearchFields();
    fetchSMSSearchFields();
    fetchAppointmentSearchFields();
    fetchLeadSearchFields();
    fetchLeadUpdateFields();
    fetchCategories();
    fetchTriggers();
    getDomainEmails();
    fetchMembers();
    getSMSPermission();
    fetchLeadOriginTypes();
    getUserSettings(SettingsKey.user_theme);
    requestPermission();
  }, []);

  const fetchLeadOriginTypes = () => {
    getLeadOriginTypes.executeQuery().then((res) => {
      let { data } = res;
      dispatch(
        setLeadOriginTypesToState(
          makeObjectFromArray(data?.leadOriginType?.items, "value", "id")
        )
      );
    });
  };

  useEffect(() => {
    // added separatley due to change in profile state. if we call this on above useEffect, it was runnig multiple times,
    if (user?.CompanyKey) {
      fetchCompanyCategory(user?.CompanyKey);
    }
  }, [user?.CompanyKey]);

  const handleCloseModal = (e, reason) => {
    console.log("reason", reason);
    if (reason === "backdropClick") {
      return false;
    } else {
      setShowSessionModal(false);
    }
  };

  const memoizedLoading = useMemo(() => {
    return loading ? (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ marginTop: "-10px" }}
      >
        <UilCircularLoader />
      </div>
    ) : null;
  }, [loading]);

  return (
    <ThemeProvider theme={theme}>
      {!loading && <AppNavBar />}
      <Box className="main-content" sx={{ mx: 0 }}>
        <Toolbar />
        {memoizedLoading}
        {
          // <div
          //   style={{ textAlign: "center", height: "100px", marginTop: "1rem" }}
          // >
          //   <CircularProgress size={"60px"} />
          // </div>
          !loading && children
        }
      </Box>
      <TourWrapper />
      <SessionExpirePrompt
        open={showSessionModal}
        message={"Your session has expired, please login again."}
        onClose={handleCloseModal}
        saveCallback={logoutHandler}
      />
    </ThemeProvider>
  );
}
